import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import SydModal from '../../../../commonDesign/SydModal'
import OpalPresentationTable from '../../../DenaliAnalysis/tables/presentation'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { useOrionIdentifier, useOrionDomainQuery } from '../../../../../api/coreData'

function buildTransactionsCalc (row, dateParams, reportCategoryId) {
  const sharedContext = { ...dateParams, reportCategoryId }

  if (row.activityOptions) {
    return {
      $type: 'grouping',
      grouping: 'ActivityOptions',
      id: 'by-activity',
      contextChange: sharedContext,
      contextChanges: row.activityOptions.map(opt => ({ activityOption: opt })),
      calculations: [
        { $type: 'activity-transactions', id: 'transactions' }
      ]
    }
  }

  return {
    $type: 'activity-transactions',
    id: 'transactions',
    contextChange: { ...sharedContext, activityOption: row.activityOption }
  }
}

function buildDrillDownRequest (row, config, { entity, entityIds, asOfDate }) {
  const dateRange = config.dateRanges?.default ?? { quickDate: 'YTD' }
  const isCustom = dateRange.quickDate === 'Custom' && dateRange.startDate && dateRange.endDate
  const dateParams = isCustom
    ? { startDate: dateRange.startDate, endDate: dateRange.endDate }
    : { quickDate: dateRange.quickDate && dateRange.quickDate !== 'Custom' ? dateRange.quickDate : 'YTD' }

  return {
    entity,
    entityIds,
    asOfDate,
    calculations: [
      {
        $type: 'grouping',
        grouping: 'Account',
        id: `drill-${row.key}`,
        calculations: [
          {
            $type: 'group-info',
            id: 'acct-number',
            entity: 'Account',
            property: 'Number'
          },
          {
            $type: 'grouping',
            grouping: 'Product',
            id: 'by-product',
            calculations: [
              {
                $type: 'group-info',
                id: 'ticker',
                entity: 'Product',
                property: 'Ticker'
              },
              buildTransactionsCalc(row, dateParams, config.reportCategoryId)
            ]
          }
        ]
      }
    ]
  }
}

function findGroupInfo (calculations, property) {
  return calculations?.find(c =>
    c.$type === 'calculated-group-info' && c.property === property
  )?.value ?? ''
}

function collectTransactions (calculations, base, result) {
  if (!calculations) return
  for (const calc of calculations) {
    if (calc.$type === 'calculated-activity-transactions' || calc.transactions) {
      for (const tx of calc.transactions ?? []) {
        result.push({ ...tx, ...base })
      }
    } else if (calc.$type === 'calculated-list' || calc.$type === 'calculated-group' || calc.$type === 'calculated-context-change') {
      collectTransactions(calc.calculations, base, result)
    }
  }
}

function flattenGroupedTransactions (response) {
  const orionData = response?.data ?? response
  if (!orionData?.calculations) return []

  const accountList = orionData.calculations.find(c =>
    c.$type === 'calculated-list' || c.calculations
  ) ?? orionData.calculations[0]

  const accountGroups = accountList?.calculations ?? orionData.calculations
  const result = []

  for (const acctGroup of accountGroups) {
    if (acctGroup.$type !== 'calculated-group') continue
    const accountName = acctGroup.name ?? ''
    const accountNumber = findGroupInfo(acctGroup.calculations, 'Number')

    const productList = acctGroup.calculations?.find(c =>
      c.$type === 'calculated-list' || (c.$type === 'calculated-group' && c.grouping === 'Product')
    )
    const productGroups = productList?.calculations ?? []

    for (const prodGroup of productGroups) {
      if (prodGroup.$type !== 'calculated-group') continue
      const productName = prodGroup.name ?? ''
      const ticker = findGroupInfo(prodGroup.calculations, 'Ticker')
      const base = { accountName, accountNumber, productName, ticker }

      collectTransactions(prodGroup.calculations, base, result)
    }
  }

  return result
}

function buildColumns (formatter) {
  return [
    {
      id: 'date',
      header: 'Date',
      accessorKey: 'startDate',
      size: 110,
      cell: (info) => info.getValue() ? dayjs(info.getValue()).format('MM/DD/YYYY') : ''
    },
    {
      id: 'accountName',
      header: 'Account',
      accessorKey: 'accountName',
      size: 180
    },
    {
      id: 'accountNumber',
      header: 'Acct #',
      accessorKey: 'accountNumber',
      size: 120
    },
    {
      id: 'product',
      header: 'Product',
      accessorKey: 'productName',
      size: 200
    },
    {
      id: 'ticker',
      header: 'Ticker',
      accessorKey: 'ticker',
      size: 80
    },
    {
      id: 'transType',
      header: 'Type',
      accessorKey: 'transactionType',
      size: 140
    },
    {
      id: 'units',
      header: 'Units',
      accessorKey: 'units',
      size: 100,
      align: 'right',
      cell: (info) => {
        const v = info.getValue()
        return (
          <span style={{ textAlign: 'right', display: 'inline-block', width: '100%' }}>
            {v != null ? formatter(v, 'number') : ''}
          </span>
        )
      }
    },
    {
      id: 'price',
      header: 'Price',
      accessorKey: 'price',
      size: 100,
      align: 'right',
      cell: (info) => {
        const v = info.getValue()
        return (
          <span style={{ textAlign: 'right', display: 'inline-block', width: '100%' }}>
            {v != null ? formatter(v, 'currency') : ''}
          </span>
        )
      }
    },
    {
      id: 'amount',
      header: 'Amount',
      accessorKey: 'value',
      size: 130,
      align: 'right',
      cell: (info) => {
        const v = info.getValue()
        return (
          <span style={{ textAlign: 'right', display: 'inline-block', width: '100%' }}>
            {v != null ? formatter(v, '$0,0.00') : ''}
          </span>
        )
      }
    }
  ]
}

function DrillDownContent ({ row, config, clientId }) {
  const { formatter } = useFormattingContext()
  const { data: identity } = useOrionIdentifier(clientId)

  const asOfDate = useMemo(
    () => dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
    []
  )

  const request = useMemo(() => {
    if (!identity?.entity || !(row?.activityOption || row?.activityOptions)) return null
    return buildDrillDownRequest(row, config, {
      entity: identity.entity,
      entityIds: identity.entityIds,
      asOfDate
    })
  }, [row, config, identity, asOfDate])

  const { data: rawResponse, isFetching } = useOrionDomainQuery(
    'reporting/scope', clientId, request,
    { enabled: !!request }
  )

  const transactions = useMemo(
    () => flattenGroupedTransactions(rawResponse),
    [rawResponse]
  )

  const columns = useMemo(() => buildColumns(formatter), [formatter])

  return (
    <OpalPresentationTable
      data={transactions}
      columns={columns}
      isLoading={isFetching}
      defaultSort={[{ id: 'date', desc: true }]}
      emptyStateText='No transactions found'
    />
  )
}

DrillDownContent.propTypes = {
  row: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

function DrillDownDialog ({ open, onClose, row, config, clientId }) {
  const title = row ? `${row.name} — Transactions` : 'Transactions'

  return (
    <SydModal
      open={open}
      onClose={onClose}
      title={title}
      size='large'
      position='top'
      allowMaximize
    >
      {row && (
        <DrillDownContent
          row={row}
          config={config}
          clientId={clientId}
        />
      )}
    </SydModal>
  )
}

DrillDownDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object,
  config: PropTypes.object.isRequired,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default DrillDownDialog
